import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import serverVars from 'server-vars';
import { getRecentHistoryList } from 'dibs-recent-history/exports/getRecentHistory';
import { SharedRecentlyViewedItemsCarousel } from './SharedRecentlyViewedItemsCarousel';
import { SharedRecentlyViewedItemsSwiper } from '../SharedRecentlyViewedItemsSwiper/SharedRecentlyViewedItemsSwiper';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import styles from './SharedRecentlyViewedItems.scss';
import {
    VERTICAL_OFFSET_RESP_IMG,
    HORIZONTAL_OFFSET_RESP_IMG,
} from '../../constants/imageConstants';

const SWIPER_ITEMS_PER_PAGE = 4.5;
const recentlyViewed = (
    <FormattedMessage id="shared.recentlyViewed" defaultMessage="Recently Viewed" />
);

export const SharedRecentlyViewedItemsComponent = ({
    itemId,
    isClient,
    classNames,
    showFullHeader,
    itemsPerPage,
    user,
    onViewMoreCtaClick,
}) => {
    const recentHistoryItems = (user?.recentHistoryItems?.edges || []).map(({ node }) => node);
    const items = getRecentHistoryList({ itemId, recentHistoryItems, isClient });
    if (!isClient || !items || !items.length) {
        return null;
    }

    const itemClasses = classnames(styles.item, { [styles.center]: items && items.length < 3 });
    const isTablet = serverVars.get('settings.isTablet');
    const maxCarouselItemLength = (isTablet ? SWIPER_ITEMS_PER_PAGE : itemsPerPage) * 2;
    const showViewMoreCta = items.length >= maxCarouselItemLength;
    const titleClassName = classNames.title || styles.title;

    const header = showFullHeader ? (
        <div className={styles.headerContainer}>
            <div className={titleClassName}>{recentlyViewed}</div>
            {showViewMoreCta && (
                <a
                    className={styles.viewMoreLink}
                    href="/favorites/viewed/"
                    onClick={onViewMoreCtaClick}
                >
                    <FormattedMessage id="shared.viewMoreCta" defaultMessage="View More" />
                </a>
            )}
        </div>
    ) : (
        <p className={titleClassName}>{recentlyViewed}</p>
    );
    return (
        <Fragment>
            {header}
            {isTablet ? (
                <SharedRecentlyViewedItemsSwiper
                    items={items}
                    itemsPerPage={SWIPER_ITEMS_PER_PAGE}
                    offsetVertical={VERTICAL_OFFSET_RESP_IMG}
                    offsetHorizontal={HORIZONTAL_OFFSET_RESP_IMG}
                    classNames={{
                        wrapper: classNames.wrapper || styles.swiperWrapper,
                        item: itemClasses,
                        list: items && items.length > 5 ? '' : styles.list,
                        placeholder: classNames.placeholder || styles.placeholder,
                        itemImage: classNames.itemImage || styles.itemImage,
                    }}
                />
            ) : (
                <SharedRecentlyViewedItemsCarousel
                    items={items}
                    itemsPerPage={itemsPerPage}
                    classNames={{
                        list: items && items.length > itemsPerPage ? '' : styles.list,
                        placeholder: styles.placeholder,
                        itemLink: styles.itemLink,
                        itemImage: styles.itemImage,
                        arrow: classNames.arrow,
                        wrapper: classNames.wrapper || styles.carouselWrapper,
                    }}
                />
            )}
        </Fragment>
    );
};

SharedRecentlyViewedItemsComponent.defaultProps = {
    showFullHeader: true,
    itemsPerPage: 6,
    onViewMoreCtaClick: () => {},
    classNames: {},
};

SharedRecentlyViewedItemsComponent.propTypes = {
    classNames: PropTypes.shape({
        wrapper: PropTypes.string,
        arrow: PropTypes.string,
        placeholder: PropTypes.string,
        itemImage: PropTypes.string,
        title: PropTypes.string,
    }),
    itemId: PropTypes.string,
    isClient: PropTypes.bool.isRequired,
    showFullHeader: PropTypes.bool.isRequired,
    itemsPerPage: PropTypes.number.isRequired,
    user: PropTypes.object,
    onViewMoreCtaClick: PropTypes.func,
};

export const SharedRecentlyViewedItems = createFragmentContainer(
    SharedRecentlyViewedItemsComponent,
    {
        user: graphql`
            fragment SharedRecentlyViewedItems_user on User {
                ...getRecentHistory_user @relay(mask: false)
            }
        `,
    }
);
